import React from 'react';
import { Helmet } from 'react-helmet';

const MathCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-purple-100">
      <Helmet>
        <title>Math Calculators | Comprehensive Mathematical Tools</title>
        <meta name="description" content="Collection of 668 math calculators for various mathematical calculations. From basic arithmetic to advanced calculus, our tools make math accessible and understandable." />
        <meta name="keywords" content="math calculators, percentage calculator, algebra calculator, geometry calculator, statistics calculator, calculus calculator, mathematical tools" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">📐</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Math Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">668 calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Maths is always daunting, there's no way around it. But thanks to our collection of maths calculators, everyone can perform and understand useful mathematical calculations in seconds. Are you scared of trigonometry? Do you think geometry is "too complicated"? Fear not! Our comprehensive array of calculators is designed to make complex mathematical concepts accessible and understandable.
            </p>
            <button className="read-more inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Percentages Calculators <span className="emoji ml-2">💯</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/average-percentage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Average Percentage Calculator</h3>
                <p className="text-gray-600">Calculate the average percentage from a set of values</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/fraction-to-percent" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Fraction to Percent Calculator</h3>
                <p className="text-gray-600">Convert fractions to percentages easily</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/decimal-to-percent" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Decimal to Percent Converter</h3>
                <p className="text-gray-600">Transform decimal numbers into percentages</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/doubling-time" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Doubling Time Calculator</h3>
                <p className="text-gray-600">Find out how long it takes for a value to double</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/percentage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Percentage Calculator</h3>
                <p className="text-gray-600">Basic percentage calculations made simple</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/percentage-increase" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Percentage Increase Classic</h3>
                <p className="text-gray-600">Calculate the percentage increase between values</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/percentage-of-percentage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Percentage of a Percentage Calculator</h3>
                <p className="text-gray-600">Calculate nested percentages with ease</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/percentage-point" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Percentage Point Calculator</h3>
                <p className="text-gray-600">Understand and calculate percentage point differences</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Geometry Calculators <span className="emoji ml-2">📐</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/area" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Area Calculator</h3>
                <p className="text-gray-600">Calculate area for various geometric shapes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/volume" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Volume Calculator</h3>
                <p className="text-gray-600">Calculate volume for 3D geometric shapes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/pythagorean" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pythagorean Theorem Calculator</h3>
                <p className="text-gray-600">Solve problems related to right-angled triangles</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/circle" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Circle Calculator</h3>
                <p className="text-gray-600">Calculate properties of circles</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/triangle" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Triangle Calculator</h3>
                <p className="text-gray-600">Calculate properties of triangles</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/polygon" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Polygon Calculator</h3>
                <p className="text-gray-600">Calculate properties of polygons</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Algebra Calculators <span className="emoji ml-2">🔢</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/quadratic" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quadratic Formula Calculator</h3>
                <p className="text-gray-600">Solve quadratic equations</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/system-of-equations" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">System of Equations Calculator</h3>
                <p className="text-gray-600">Solve systems of linear equations</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/matrix" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Matrix Calculator</h3>
                <p className="text-gray-600">Perform matrix operations</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/polynomials" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Polynomial Calculator</h3>
                <p className="text-gray-600">Perform operations on polynomials</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/factorial" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Factorial Calculator</h3>
                <p className="text-gray-600">Calculate factorials of numbers</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/sequence" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Sequence Calculator</h3>
                <p className="text-gray-600">Analyze number sequences</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Statistics Calculators <span className="emoji ml-2">📊</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mean-median-mode" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mean, Median, Mode Calculator</h3>
                <p className="text-gray-600">Calculate central tendency measures</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/standard-deviation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Standard Deviation Calculator</h3>
                <p className="text-gray-600">Calculate variability in datasets</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/correlation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Correlation Calculator</h3>
                <p className="text-gray-600">Measure relationship between variables</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/probability" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Probability Calculator</h3>
                <p className="text-gray-600">Calculate probabilities of events</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/combinatorics" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Combinatorics Calculator</h3>
                <p className="text-gray-600">Calculate permutations and combinations</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/regression" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Regression Calculator</h3>
                <p className="text-gray-600">Perform regression analysis</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Calculus Calculators <span className="emoji ml-2">∮</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/derivative" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Derivative Calculator</h3>
                <p className="text-gray-600">Calculate derivatives of functions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/integral" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Integral Calculator</h3>
                <p className="text-gray-600">Calculate definite and indefinite integrals</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/limits" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Limit Calculator</h3>
                <p className="text-gray-600">Evaluate limits of functions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/series" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Series Calculator</h3>
                <p className="text-gray-600">Analyze infinite series convergence</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/taylor-series" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Taylor Series Calculator</h3>
                <p className="text-gray-600">Calculate Taylor series expansions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/differential-equations" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Differential Equations Calculator</h3>
                <p className="text-gray-600">Solve ordinary differential equations</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathCalculators;